import React from 'react';
import { Box, Typography } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { useTokens } from 'theme';
import BarChart from 'components/common/BarChart';
import Panel from './Panel';
import { channelMeta } from './channels';

import { useTranslation } from 'react-i18next';

function TopCubes({ cubes }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  if (!cubes?.length) {
    return (
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: themeTokens.inkMuted, py: 1 }}>{t('data.noQueriesRecordedYet')}</Typography>
    );
  }
  const max = Math.max(1, ...cubes.map((c) => c.count));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {cubes.map((c, i) => (
        <Box key={c.cube_name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: themeTokens.inkMuted, width: 16 }}>{i + 1}</Typography>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography className="mono" sx={{ fontSize: '0.78rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.cube_name}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: themeTokens.inkMuted, ml: 1 }}>{c.count}</Typography>
            </Box>
            <Box sx={{ height: 5, bgcolor: themeTokens.raised, borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: `${(c.count / max) * 100}%`, bgcolor: themeTokens.series[0], borderRadius: 3 }} />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function BySurface({ byChannel }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  const rows = ['cube', 'trino', 'mcp'].map((key) => ({ key, count: byChannel?.[key] ?? 0 }));
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  if (!total) {
    return <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: themeTokens.inkMuted, py: 1 }}>{t('data.noTrafficYet')}</Typography>;
  }
  const metaByKey = channelMeta(themeTokens);
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {rows.map((r) => {
        const meta = metaByKey[r.key];
        const Icon = meta.Icon;
        return (
          <Box key={r.key}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Icon style={{ fontSize: 15, color: meta.color }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{meta.label}</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: themeTokens.inkMuted }}>{r.count}</Typography>
            </Box>
            <Box sx={{ height: 6, bgcolor: themeTokens.raised, borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: `${(r.count / max) * 100}%`, bgcolor: meta.color, borderRadius: 3 }} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default function UsageTrend({ usage }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Panel icon={<TimelineIcon />} title={t('data.queryVolume14Days')}>
        <BarChart
          series={usage?.timeseries ?? []}
          total={(d) => d.total}
          part={(d) => d.error}
          tooltip={(d) => t('data.valueValueQueriesValueOkValueErr', { value1: d.date, value2: d.total, value3: d.success, value4: d.error })}
          emptyText={t('data.noQueriesInTheLast14Days')}
          totalLabel={t('data.actionSuccess')}
          partLabel={t('data.error')}
        />
      </Panel>
      <Panel icon={<CallSplitIcon />} title={t('data.trafficBySurface')}>
        <BySurface byChannel={usage?.by_channel} />
      </Panel>
      <Panel icon={<LeaderboardIcon />} title={t('data.topCubes')}>
        <TopCubes cubes={usage?.top_cubes ?? []} />
      </Panel>
    </Box>
  );
}
